import { useEffect, useState } from 'react';

export type InstallContext =
  | 'standalone'        // already installed and running from home screen — never show prompt
  | 'ios-safari'        // can install via Safari Share → Add to Home Screen
  | 'ios-other-browser' // iOS Chrome/Firefox/Edge — need to redirect to Safari
  | 'ios-in-app'        // stuck inside Instagram / FB / TikTok webview
  | 'android'           // can use one-tap beforeinstallprompt
  | 'android-in-app'    // Android in-app webview — should open in Chrome
  | 'desktop'           // not mobile — skip prompt
  | 'other';            // something we don't handle

const SEEN_KEY = 'install_prompt_seen';

function detectStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  if ((window.navigator as any).standalone === true) return true;
  return false;
}

function detectInAppBrowser(ua: string): boolean {
  return /(instagram|fbav|fban|fb_iab|fbios|tiktok|line|micromessenger|wechat|snapchat|twitter|linkedin)/i.test(ua);
}

function detectIOS(ua: string): boolean {
  if (/iphone|ipad|ipod/i.test(ua)) return true;
  // iPadOS 13+ reports Mac UA but exposes touch points
  if (/macintosh/i.test(ua) && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1) return true;
  return false;
}

function detectIOSSafari(ua: string): boolean {
  if (!detectIOS(ua)) return false;
  // iOS Chrome/Firefox/Edge identify as crios/fxios/edgios — exclude them.
  if (/crios|fxios|edgios|opt|opios|gsa/i.test(ua)) return false;
  return /safari/i.test(ua);
}

function detectAndroid(ua: string): boolean {
  return /android/i.test(ua);
}

export function detectInstallContext(): InstallContext {
  if (typeof navigator === 'undefined') return 'other';
  if (detectStandalone()) return 'standalone';

  const ua = navigator.userAgent;
  const inApp = detectInAppBrowser(ua);

  if (detectIOS(ua)) {
    if (inApp) return 'ios-in-app';
    if (detectIOSSafari(ua)) return 'ios-safari';
    return 'ios-other-browser';
  }
  if (detectAndroid(ua)) {
    if (inApp) return 'android-in-app';
    return 'android';
  }

  return 'desktop';
}

export function hasSeenInstallPrompt(): boolean {
  try { return localStorage.getItem(SEEN_KEY) === '1'; } catch { return false; }
}

export function markInstallPromptSeen() {
  try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* ignore */ }
}

export function clearInstallPromptSeen() {
  try { localStorage.removeItem(SEEN_KEY); } catch { /* ignore */ }
}

/**
 * True when we should show the install prompt page before onboarding.
 * Includes iOS (Safari / other / in-app) and Android (Chrome / in-app) cases.
 */
export function shouldGateOnInstall(ctx: InstallContext): boolean {
  if (ctx === 'standalone') return false;
  if (ctx === 'desktop' || ctx === 'other') return false;
  if (hasSeenInstallPrompt()) return false;
  return true;
}

export function useInstallEligibility() {
  // null = not yet detected (before first useEffect). Avoids incorrectly treating
  // the pre-detection 'other' default as ineligible and bouncing to /onboarding.
  const [ctx, setCtx] = useState<InstallContext | null>(null);

  useEffect(() => {
    setCtx(detectInstallContext());
    const mq = window.matchMedia?.('(display-mode: standalone)');
    if (!mq) return;
    const onChange = () => setCtx(detectInstallContext());
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  return ctx;
}
